import { Button, DatePicker, Flex, Form, Input } from "antd";
import TextArea from "antd/es/input/TextArea";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AddIcon from "../../../../assets/icons/AddIcon";
import TitleBar from "../../../../components/common/TitleBar";
import {
  useCreateKpiGroupMutation,
  useGetSingleKPIGroupQuery,
  useUpdateKPIGroupMutation,
} from "../../../../features/organization/contractual/KPISetup/kpiSetupApi";
import useError from "../../../../hooks/useError";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { disablePastDates } from "../../../../lib/time";
import { toast } from "../../../../lib/toast";
import { validatePositiveNumber } from "../../../../utils/validators";
import CalanderIcon from "../../survey/templates/_icons/CalanderIcon";

const ManageKPI = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const isUpdating = Boolean(id);

  const { data } = useGetSingleKPIGroupQuery(
    id as unknown as number as number,
    { skip: !id }
  );

  const [
    createKPIGroup,
    {
      isLoading: creating,
      isSuccess: created,
      isError: isCreateError,
      error: createError,
    },
  ] = useCreateKpiGroupMutation();
  const [
    updateKPIGroup,
    {
      isLoading: updating,
      isSuccess: updated,
      isError: isUpdateError,
      error: updateError,
    },
  ] = useUpdateKPIGroupMutation();

  const handleSubmit = (values: any) => {
    if (!isUpdating) {
      createKPIGroup(values);
    } else {
      updateKPIGroup({ data: values, id });
    }
  };

  // ------ Effects ------
  useEffect(() => {
    if (data?.data.group) {
      form.setFieldsValue({
        ...data.data.group,
        effective_from: dayjs(data?.data?.group?.effective_from),
      });
    }
  }, [data, form]);

  useEffect(() => {
    if (created) {
      toast("success", "KPI group created successfully.");
      form.resetFields();
      navigate("/organization/contractual/kpi-setup");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [created]);

  useEffect(() => {
    if (updated) {
      toast("success", "KPI group updated successfully.");
      form.resetFields();
      navigate("/organization/contractual/kpi-setup");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updated]);

  // ------ Errors ------
  useError(isCreateError, createError, "KPI group create failed.");
  useError(isUpdateError, updateError, "KPI group update failed.");
  return (
    <SidebarLayout title="KPI">
      <TitleBar backLink={-1 as unknown as string} title="Add New KPI Group" />

      <Form
        form={form}
        size="large"
        layout="vertical"
        requiredMark="optional"
        className="custom-form space-y-5"
        onFinish={handleSubmit}
      >
        {/* KPI Group Info */}
        <div className="p-4 bg-white rounded-lg grid grid-cols-1 md:grid-cols-2 gap-4">
          <Form.Item
            name="group_name"
            label="KPI Group Name"
            className="mb-0"
            rules={[
              { required: true, message: "Please enter kpi group name" },
              {
                max: 50,
                message: "KPI group name cannot exceed 50 characters",
              },
            ]}
          >
            <Input placeholder="Enter KPI group name" />
          </Form.Item>
          <Form.Item
            name="effective_from"
            label="Effective Date"
            className="mb-0"
            rules={[
              { required: true, message: "Please select effective date" },
            ]}
          >
            <DatePicker
              className="w-full"
              format={"YYYY-MM-DD"}
              placeholder={"dd/mm/yyyy"}
              suffixIcon={<CalanderIcon />}
              disabledDate={disablePastDates}
            />
          </Form.Item>
        </div>

        <Form.List name="kpis" initialValue={[{}]}>
          {(fields, { add, remove }) => (
            <div className="space-y-4">
              {fields.map(({ key, name, ...restField }) => (
                <div
                  key={key}
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4 rounded-lg relative bg-white"
                >
                  <Form.Item
                    {...restField}
                    name={[name, "kpi_name"]}
                    label="KPI Name"
                    className="mb-0"
                    rules={[
                      { required: true, message: "Please enter kpi name" },
                      {
                        max: 50,
                        message: "KPI name cannot exceed 50 characters",
                      },
                    ]}
                  >
                    <Input placeholder="Enter KPI name" />
                  </Form.Item>

                  <Form.Item
                    {...restField}
                    name={[name, "weightage_percentage"]}
                    label="Weightage (%)"
                    className="mb-0"
                    rules={[
                      { required: true, message: "Please enter weightage" },
                      { validator: validatePositiveNumber },
                      {
                        validator: (_, value) => {
                          if (value > 100) {
                            return Promise.reject(
                              new Error("Weightage cannot be more than 100")
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input placeholder="Enter weightage" type="number" />
                  </Form.Item>

                  <Form.Item
                    {...restField}
                    name={[name, "threshold_percentage"]}
                    label="Threshold (%)"
                    className="mb-0"
                    rules={[
                      { required: true, message: "Please enter threshold" },
                      { validator: validatePositiveNumber },
                      {
                        validator: (_, value) => {
                          if (value > 100) {
                            return Promise.reject(
                              new Error("Weightage cannot be more than 100")
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input placeholder="Enter threshold" type="number" />
                  </Form.Item>

                  <div className="col-span-1 md:col-span-2 lg:col-span-3">
                    <Form.Item
                      {...restField}
                      name={[name, "description"]}
                      label="Description"
                      className="mb-2"
                      rules={[
                        { required: true, message: "Please enter description" },
                        {
                          validator: (_, value) => {
                            if (value && value.length > 150) {
                              return Promise.reject(
                                "Description cannot exceed 150 characters"
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <TextArea
                        rows={4}
                        maxLength={150}
                        showCount
                        placeholder="Enter description..."
                      />
                    </Form.Item>
                    <div className="text-body-text text-sm font-normal mb-0">
                      Tip: clear, simple, and under 150 Characters
                    </div>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={() => add()}
                className="flex items-center gap-2 text-primary"
              >
                <AddIcon /> Add KPI
              </button>
            </div>
          )}
        </Form.List>

        <Flex align="center" gap={16}>
          <Button
            htmlType="button"
            className="h-10 min-w-[120px]"
            onClick={() => navigate("/organization/contractual/kpi-setup")}
          >
            Cancel
          </Button>
          <Button
            size="middle"
            type="primary"
            htmlType="submit"
            loading={creating || updating}
            className="h-10 min-w-[120px]"
          >
            {isUpdating ? "Update" : "Create"}
          </Button>
        </Flex>
      </Form>
    </SidebarLayout>
  );
};

export default ManageKPI;
